import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Icon from '../components/Icon';
import { formatDateWithTime } from '../utils/format';
import { markSupportConversationsFeatureSeen } from '../utils/supportFeatureBadge';
import Skeleton, { SkeletonList } from '../components/Skeleton';

const CATEGORY_OPTIONS = [
  { value: 'account', label: 'Account' },
  { value: 'payment', label: 'Payment' },
  { value: 'property', label: 'Property' },
  { value: 'groups', label: 'Groups' },
  { value: 'technical', label: 'Technical' },
  { value: 'other', label: 'Other' },
];

const STATUS_STYLES = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-800',
  waiting_on_user: 'bg-purple-100 text-purple-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-200 text-gray-700',
};

const PRIORITY_STYLES = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-sky-100 text-sky-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

function SupportTicketsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [replyingId, setReplyingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({});
  const [form, setForm] = useState({
    category: 'other',
    subject: '',
    description: '',
  });

  const openTickets = useMemo(
    () => tickets.filter((ticket) => ['open', 'in_progress', 'waiting_on_user'].includes(ticket.status)),
    [tickets]
  );
  const resolvedTickets = useMemo(
    () => tickets.filter((ticket) => ['resolved', 'closed'].includes(ticket.status)),
    [tickets]
  );
  const unreadUpdatesCount = useMemo(
    () => tickets.filter((ticket) => ticket.unreadForUser).length,
    [tickets]
  );
  const selectedTicket = useMemo(
    () => tickets.find((ticket) => ticket._id === selectedTicketId) || null,
    [tickets, selectedTicketId]
  );

  const fetchPageData = useCallback(async () => {
    try {
      setLoading(true);
      const [userRes, ticketsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/auth/support-tickets/mine'),
      ]);

      if (userRes?.data?.locked) {
        navigate('/locked');
        return;
      }

      const nextTickets = Array.isArray(ticketsRes?.data?.tickets) ? ticketsRes.data.tickets : [];
      const nextUser = userRes?.data || null;

      setUser(nextUser);
      if (nextUser?._id && nextUser?.role !== 'A') {
        markSupportConversationsFeatureSeen(nextUser._id);
      }
      setTickets(nextTickets);
      setSelectedTicketId((prev) => {
        if (nextTickets.some((ticket) => ticket._id === prev)) return prev;
        return nextTickets[0]?._id || null;
      });
      setReplyDrafts((prev) => {
        const next = {};
        nextTickets.forEach((ticket) => {
          next[ticket._id] = prev[ticket._id] || '';
        });
        return next;
      });
      setError('');

      await api.post('/auth/support-tickets/mark-user-viewed');
      setTickets((prev) => prev.map((ticket) => ({ ...ticket, unreadForUser: false })));
    } catch (fetchError) {
      console.error('Failed to load support tickets:', fetchError);
      if (fetchError?.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setError(fetchError?.response?.data?.error || 'Failed to load support tickets.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchPageData();
  }, [fetchPageData, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/auth/support-tickets', form);
      const newTicket = response?.data?.ticket;

      setForm({
        category: 'other',
        subject: '',
        description: '',
      });
      setSuccess('Support ticket submitted successfully.');
      if (newTicket?._id) {
        setTickets((prev) => [newTicket, ...prev]);
        setReplyDrafts((prev) => ({ ...prev, [newTicket._id]: '' }));
        setSelectedTicketId(newTicket._id);
      } else {
        await fetchPageData();
      }
    } catch (saveError) {
      console.error('Failed to submit ticket:', saveError);
      setError(saveError?.response?.data?.error || 'Failed to submit support ticket.');
    } finally {
      setSaving(false);
    }
  };

  const handleReply = async (ticketId) => {
    const message = String(replyDrafts[ticketId] || '').trim();
    if (!message) return;

    try {
      setReplyingId(ticketId);
      const response = await api.post(`/auth/support-tickets/${ticketId}/reply`, { message });
      const updatedTicket = response?.data?.ticket;

      setTickets((prev) => prev.map((ticket) => (ticket._id === ticketId ? updatedTicket : ticket)));
      setReplyDrafts((prev) => ({ ...prev, [ticketId]: '' }));
      setError('');
      setSuccess('Reply sent successfully.');
    } catch (replyError) {
      console.error('Failed to send ticket reply:', replyError);
      setError(replyError?.response?.data?.error || 'Failed to send reply.');
      setSuccess('');
    } finally {
      setReplyingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 font-sans">
        <div className="mx-auto max-w-4xl px-4 py-6" aria-busy="true" aria-label="Loading support center">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 shrink-0" rounded="rounded-xl" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-5 w-44" />
              <Skeleton className="h-3 w-28" />
            </div>
          </div>
          <SkeletonList rows={5} className="mt-5" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 font-sans">
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/90 backdrop-blur-md shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50"
              aria-label="Back to dashboard"
            >
              <Icon name="arrow-left" size="1rem" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Support Tickets</h1>
              <p className="text-xs text-gray-500 truncate">
                {user?.name || 'User'} - {openTickets.length} active ticket{openTickets.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <Icon name="house" size="1rem" />
            Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-5 sm:px-6 sm:py-6">
        <section className="mb-6 grid gap-3 sm:grid-cols-3">
          <div className="rounded-3xl border border-emerald-100 bg-white/95 p-4 shadow-sm">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Active Tickets</div>
            <div className="mt-2 text-2xl font-bold text-gray-900">{openTickets.length}</div>
            <p className="mt-1 text-sm text-gray-600">Open, in progress, or waiting on your reply.</p>
          </div>
          <div className="rounded-3xl border border-sky-100 bg-white/95 p-4 shadow-sm">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700">New Updates</div>
            <div className="mt-2 text-2xl font-bold text-gray-900">{unreadUpdatesCount}</div>
            <p className="mt-1 text-sm text-gray-600">Tickets that have a new response or status change.</p>
          </div>
          <div className="rounded-3xl border border-gray-200 bg-white/95 p-4 shadow-sm">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-600">Resolved History</div>
            <div className="mt-2 text-2xl font-bold text-gray-900">{resolvedTickets.length}</div>
            <p className="mt-1 text-sm text-gray-600">Resolved or closed tickets you can still review.</p>
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <section className="rounded-3xl border border-white/70 bg-white/95 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <Icon name="life-preserver" size="1.2rem" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Raise a Ticket</h2>
                <p className="text-sm text-gray-600">Tell the admin what you need help with.</p>
              </div>
            </div>

            {error ? (
              <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}
            {success ? (
              <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {success}
              </div>
            ) : null}

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={form.category}
                  onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                  placeholder="Briefly summarize the issue"
                  maxLength={160}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <div className="mt-2 text-right text-xs text-gray-400">{form.subject.length}/160</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={form.description}
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                  placeholder="Describe what happened, what you expected, and any useful details."
                  rows={7}
                  maxLength={5000}
                  className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <div className="mt-2 flex items-center justify-between gap-3 text-xs text-gray-400">
                  <span>Tip: include the unit, transaction ID, or date if relevant.</span>
                  <span>{form.description.length}/5000</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
              >
                <Icon name="send" size="1rem" />
                {saving ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </form>
          </section>

          <section className="rounded-3xl border border-white/70 bg-white/95 p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">My Tickets</h2>
                <p className="text-sm text-gray-600">Open a ticket to review the full conversation.</p>
              </div>
              <button
                onClick={fetchPageData}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Icon name="arrow-clockwise" size="1rem" />
                Refresh
              </button>
            </div>

            {tickets.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-500">
                No support tickets yet.
              </div>
            ) : (
              <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)]">
                <div className="space-y-3">
                  {tickets.map((ticket) => {
                    const latestMessage = Array.isArray(ticket.messages) && ticket.messages.length > 0
                      ? ticket.messages[ticket.messages.length - 1]
                      : null;
                    const isSelected = ticket._id === selectedTicketId;

                    return (
                      <button
                        key={ticket._id}
                        type="button"
                        onClick={() => setSelectedTicketId(ticket._id)}
                        className={`w-full rounded-3xl border p-4 text-left shadow-sm transition ${
                          isSelected
                            ? 'border-emerald-200 bg-emerald-50/80'
                            : 'border-gray-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/40'
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm font-semibold text-gray-900">{ticket.subject}</h3>
                          {ticket.unreadForUser ? (
                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                              New update
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 text-[11px] text-gray-500">
                          {ticket.ticketNumber} · {formatDateWithTime(ticket.createdAt)}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${STATUS_STYLES[ticket.status] || STATUS_STYLES.open}`}>
                            {ticket.status.replaceAll('_', ' ')}
                          </span>
                          <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES.medium}`}>
                            {ticket.priority}
                          </span>
                        </div>
                        <p className="mt-3 text-xs leading-5 text-gray-600">
                          {latestMessage?.message || ticket.description}
                        </p>
                      </button>
                    );
                  })}
                </div>

                {selectedTicket ? (
                  <article className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="border-b border-gray-100 pb-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-gray-900">{selectedTicket.subject}</h3>
                        {selectedTicket.unreadForUser ? (
                          <span className="rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-semibold text-red-700">
                            New update
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-500">
                        <span>{selectedTicket.ticketNumber}</span>
                        <span>{formatDateWithTime(selectedTicket.createdAt)}</span>
                        <span>{selectedTicket.category}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${STATUS_STYLES[selectedTicket.status] || STATUS_STYLES.open}`}>
                          {selectedTicket.status.replaceAll('_', ' ')}
                        </span>
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${PRIORITY_STYLES[selectedTicket.priority] || PRIORITY_STYLES.medium}`}>
                          {selectedTicket.priority}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 space-y-3">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                        Conversation
                      </div>
                      <div className="max-h-[480px] space-y-3 overflow-y-auto rounded-3xl border border-gray-200 bg-gray-50 p-4">
                        {(selectedTicket.messages || []).map((entry, index) => {
                          const isAdmin = entry.senderType === 'admin';
                          return (
                            <div
                              key={entry._id || `${selectedTicket._id}-${index}`}
                              className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[90%] rounded-3xl px-4 py-3 text-sm shadow-sm ${
                                  isAdmin
                                    ? 'bg-emerald-600 text-white'
                                    : 'border border-gray-200 bg-white text-gray-800'
                                }`}
                              >
                                <div className={`text-[11px] font-semibold ${isAdmin ? 'text-emerald-100' : 'text-gray-500'}`}>
                                  {isAdmin ? (entry.sender?.name || 'Admin') : 'You'}
                                </div>
                                <p className="mt-1 whitespace-pre-wrap leading-6">{entry.message}</p>
                                <div className={`mt-2 text-[10px] ${isAdmin ? 'text-emerald-100' : 'text-gray-400'}`}>
                                  {formatDateWithTime(entry.createdAt)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {selectedTicket.status !== 'closed' ? (
                        <div className="rounded-3xl border border-gray-200 bg-gray-50 p-4">
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                            Reply
                          </label>
                          <textarea
                            value={replyDrafts[selectedTicket._id] || ''}
                            onChange={(event) =>
                              setReplyDrafts((prev) => ({ ...prev, [selectedTicket._id]: event.target.value }))
                            }
                            rows={5}
                            className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="Add more detail or answer the admin here."
                          />
                          <button
                            onClick={() => handleReply(selectedTicket._id)}
                            disabled={replyingId === selectedTicket._id || !String(replyDrafts[selectedTicket._id] || '').trim()}
                            className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                          >
                            <Icon name="send" size="1rem" />
                            {replyingId === selectedTicket._id ? 'Sending...' : 'Send Reply'}
                          </button>
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                          This ticket is closed. Start a new ticket if you need more help.
                        </div>
                      )}
                    </div>
                  </article>
                ) : null}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

export default SupportTicketsPage;
